<script setup>
import { computed, onMounted, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';

import PageLayout from 'dashboard/components-next/pilot/PageLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const route = useRoute();
const store = useStore();

const assistantId = computed(() => Number(route.params.assistantId));

// 获取助手信息
const assistant = useMapGetter('pilotAssistants/getRecord');

// 全局资源
const globalDocuments = useMapGetter('pilotGlobalDocuments/getRecords');
const globalFaqs = useMapGetter('pilotGlobalFaqs/getRecords');
const customTools = useMapGetter('pilotCustomTools/getRecords');

// 助手已关联的资源
const assistantGlobalDocuments = ref([]);
const assistantGlobalFaqs = ref([]);
const assistantCustomTools = ref([]);

// 加载状态
const isLoadingResources = ref(false);

// 加载助手已关联的资源
const fetchAssistantResources = async () => {
  try {
    const [docsRes, faqsRes, toolsRes] = await Promise.all([
      store.dispatch('pilotAssistantGlobalDocuments/get', assistantId.value),
      store.dispatch('pilotAssistantGlobalFaqs/get', assistantId.value),
      store.dispatch('pilotAssistantCustomTools/get', assistantId.value),
    ]);

    // 从响应中提取关联的资源 ID
    assistantGlobalDocuments.value = (docsRes.payload || []).map(
      item => item.id
    );
    assistantGlobalFaqs.value = (faqsRes.payload || []).map(item => item.id);
    assistantCustomTools.value = (toolsRes.payload || []).map(item => item.id);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch assistant resources:', error);
  }
};

// 切换全局文档关联
const toggleGlobalDocument = async globalDocumentId => {
  const isAssociated =
    assistantGlobalDocuments.value.includes(globalDocumentId);

  try {
    if (isAssociated) {
      await store.dispatch('pilotAssistantGlobalDocuments/delete', {
        assistantId: assistantId.value,
        globalDocumentId,
      });
      assistantGlobalDocuments.value = assistantGlobalDocuments.value.filter(
        id => id !== globalDocumentId
      );
    } else {
      await store.dispatch('pilotAssistantGlobalDocuments/create', {
        assistantId: assistantId.value,
        globalDocumentId,
      });
      assistantGlobalDocuments.value.push(globalDocumentId);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to toggle global document:', error);
  }
};

// 切换全局 FAQ 关联
const toggleGlobalFaq = async globalFaqId => {
  const isAssociated = assistantGlobalFaqs.value.includes(globalFaqId);

  try {
    if (isAssociated) {
      await store.dispatch('pilotAssistantGlobalFaqs/delete', {
        assistantId: assistantId.value,
        globalFaqId,
      });
      assistantGlobalFaqs.value = assistantGlobalFaqs.value.filter(
        id => id !== globalFaqId
      );
    } else {
      await store.dispatch('pilotAssistantGlobalFaqs/create', {
        assistantId: assistantId.value,
        globalFaqId,
      });
      assistantGlobalFaqs.value.push(globalFaqId);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to toggle global FAQ:', error);
  }
};

// 切换自定义工具关联
const toggleCustomTool = async customToolId => {
  const isAssociated = assistantCustomTools.value.includes(customToolId);

  try {
    if (isAssociated) {
      await store.dispatch('pilotAssistantCustomTools/delete', {
        assistantId: assistantId.value,
        customToolId,
      });
      assistantCustomTools.value = assistantCustomTools.value.filter(
        id => id !== customToolId
      );
    } else {
      await store.dispatch('pilotAssistantCustomTools/create', {
        assistantId: assistantId.value,
        customToolId,
      });
      assistantCustomTools.value.push(customToolId);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to toggle custom tool:', error);
  }
};

onMounted(() => {
  fetchGlobalResources();
});
</script>

<template>
  <PageLayout
    :header-title="$t('PILOT.ASSISTANTS.OVERVIEW.TITLE')"
    :show-button="false"
  >
    <template #body>
      <div v-if="isLoadingResources" class="flex justify-center py-12">
        <Spinner />
      </div>

      <div v-else class="space-y-8">
        <!-- 助手基本信息 -->
        <div
          class="bg-n-slate-white dark:bg-n-slate-800 rounded-lg border border-n-slate-200 dark:border-n-slate-700 p-6"
        >
          <h3
            class="text-lg font-medium text-n-slate-900 dark:text-n-slate-100 mb-4"
          >
            {{ $t('PILOT.ASSISTANTS.OVERVIEW.ASSISTANT_INFO') }}
          </h3>
          <div class="space-y-2 text-sm">
            <div class="flex">
              <span class="w-32 text-n-slate-600 dark:text-n-slate-400">
                {{ $t('PILOT.ASSISTANTS.OVERVIEW.NAME') }}:
              </span>
              <span class="text-n-slate-900 dark:text-n-slate-100">
                {{ assistant?.name }}
              </span>
            </div>
            <div class="flex">
              <span class="w-32 text-n-slate-600 dark:text-n-slate-400">
                {{ $t('PILOT.ASSISTANTS.OVERVIEW.DESCRIPTION') }}:
              </span>
              <span class="text-n-slate-900 dark:text-n-slate-100">
                {{ assistant?.description || '-' }}
              </span>
            </div>
          </div>
        </div>

        <!-- 全局知识库 -->
        <div
          class="bg-n-slate-white dark:bg-n-slate-800 rounded-lg border border-n-slate-200 dark:border-n-slate-700 p-6"
        >
          <h3
            class="text-lg font-medium text-n-slate-900 dark:text-n-slate-100 mb-4"
          >
            {{ $t('PILOT.ASSISTANTS.OVERVIEW.GLOBAL_DOCUMENTS') }}
          </h3>
          <div class="space-y-2">
            <div
              v-for="doc in globalDocuments"
              :key="doc.id"
              class="flex items-center justify-between p-3 bg-n-slate-50 dark:bg-n-slate-900 rounded-lg"
            >
              <div class="flex-1">
                <div
                  class="text-sm font-medium text-n-slate-900 dark:text-n-slate-100"
                >
                  {{ doc.name || doc.external_link }}
                </div>
                <div
                  v-if="doc.content"
                  class="text-xs text-n-slate-600 dark:text-n-slate-400 truncate max-w-md"
                >
                  {{ doc.content }}
                </div>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  :checked="assistantGlobalDocuments.includes(doc.id)"
                  class="sr-only peer"
                  @change="toggleGlobalDocument(doc.id)"
                />
                <div
                  class="w-11 h-6 bg-n-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-n-blue-300 dark:peer-focus:ring-n-blue-800 rounded-full peer dark:bg-n-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-n-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-n-slate-600 peer-checked:bg-n-brand-500"
                />
              </label>
            </div>
            <div
              v-if="!globalDocuments || globalDocuments.length === 0"
              class="text-sm text-n-slate-500 dark:text-n-slate-500 text-center py-4"
            >
              {{ $t('PILOT.ASSISTANTS.OVERVIEW.NO_GLOBAL_DOCUMENTS') }}
            </div>
          </div>
        </div>

        <!-- 全局 FAQ -->
        <div
          class="bg-n-slate-white dark:bg-n-slate-800 rounded-lg border border-n-slate-200 dark:border-n-slate-700 p-6"
        >
          <h3
            class="text-lg font-medium text-n-slate-900 dark:text-n-slate-100 mb-4"
          >
            {{ $t('PILOT.ASSISTANTS.OVERVIEW.GLOBAL_FAQS') }}
          </h3>
          <div class="space-y-2">
            <div
              v-for="faq in globalFaqs"
              :key="faq.id"
              class="flex items-center justify-between p-3 bg-n-slate-50 dark:bg-n-slate-900 rounded-lg"
            >
              <div class="flex-1">
                <div
                  class="text-sm font-medium text-n-slate-900 dark:text-n-slate-100"
                >
                  {{ faq.question }}
                </div>
                <div
                  class="text-xs text-n-slate-600 dark:text-n-slate-400 truncate max-w-md"
                >
                  {{ faq.answer }}
                </div>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  :checked="assistantGlobalFaqs.includes(faq.id)"
                  class="sr-only peer"
                  @change="toggleGlobalFaq(faq.id)"
                />
                <div
                  class="w-11 h-6 bg-n-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-n-blue-300 dark:peer-focus:ring-n-blue-800 rounded-full peer dark:bg-n-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-n-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-n-slate-600 peer-checked:bg-n-brand-500"
                />
              </label>
            </div>
            <div
              v-if="!globalFaqs || globalFaqs.length === 0"
              class="text-sm text-n-slate-500 dark:text-n-slate-500 text-center py-4"
            >
              {{ $t('PILOT.ASSISTANTS.OVERVIEW.NO_GLOBAL_FAQS') }}
            </div>
          </div>
        </div>

        <!-- 自定义工具 -->
        <div
          class="bg-n-slate-white dark:bg-n-slate-800 rounded-lg border border-n-slate-200 dark:border-n-slate-700 p-6"
        >
          <h3
            class="text-lg font-medium text-n-slate-900 dark:text-n-slate-100 mb-4"
          >
            {{ $t('PILOT.ASSISTANTS.OVERVIEW.CUSTOM_TOOLS') }}
          </h3>
          <div class="space-y-2">
            <div
              v-for="tool in customTools"
              :key="tool.id"
              class="flex items-center justify-between p-3 bg-n-slate-50 dark:bg-n-slate-900 rounded-lg"
            >
              <div class="flex-1">
                <div
                  class="text-sm font-medium text-n-slate-900 dark:text-n-slate-100"
                >
                  {{ tool.title }}
                </div>
                <div class="text-xs text-n-slate-600 dark:text-n-slate-400">
                  {{ tool.code }}
                </div>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  :checked="assistantCustomTools.includes(tool.id)"
                  class="sr-only peer"
                  @change="toggleCustomTool(tool.id)"
                />
                <div
                  class="w-11 h-6 bg-n-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-n-blue-300 dark:peer-focus:ring-n-blue-800 rounded-full peer dark:bg-n-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-n-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-n-slate-600 peer-checked:bg-n-brand-500"
                />
              </label>
            </div>
            <div
              v-if="!customTools || customTools.length === 0"
              class="text-sm text-n-slate-500 dark:text-n-slate-500 text-center py-4"
            >
              {{ $t('PILOT.ASSISTANTS.OVERVIEW.NO_CUSTOM_TOOLS') }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </PageLayout>
</template>
